/* eslint-disable no-cond-assign */

import { ProposalDAOInterface } from '../ProposalDAO';
import { ComparisonItemBasedProposalDAO } from '../ProposalDAOImpl';
import { ProposalFieldDAO } from '../ProposalFieldDAO';
import * as ProposalNS from '../../Proposal';
import * as ProposalElementNS from '../../ProposalElements';
import { DataPoints, DataPointResponse } from '../DataPoints';
import { ASSET_ALLOCATION_GROUPS } from '../../../../utils/constants';
import { ComparisonItemDAO } from '../ProposalItemDAO';
import { ProposalStyleClassNames } from '../../ProposalStyleClassNames';
import { formatMessageWithLocale } from '../../../../utils/i18n';
import { NotApplicableCell, PercentageCell, TextCell } from '../../../../views/components/cells';
import Dictionary from '@/types/dictionary';
import LocalizedString from '@/types/localized-string';

export default class CoreEquityMetricsDAO extends ComparisonItemBasedProposalDAO {
  labelOverride?: Dictionary<LocalizedString>;

  public static buildInstance(proposal: ProposalNS.Proposal, proposalElement: ProposalElementNS.ProposalDataElement): ProposalDAOInterface {
    return new CoreEquityMetricsDAO(proposal, proposalElement);
  }

  /* eslint-disable-next-line class-methods-use-this */
  protected getColorKeys(): string[] {
    return ['CORE_EQUITY_METRICS'];
  }

  protected build(): void {
    const daoConfig: ProposalNS.CoreEquityMetricsConfig = this.proposalElement.DAO as ProposalNS.CoreEquityMetricsConfig;

    if (daoConfig.labelOverride !== undefined) {
      this.labelOverride = daoConfig.labelOverride;
    }

    let itemDAO = null;
    while ((itemDAO = this.nextItem()) !== null) {
      const dpResponseAA: DataPointResponse<number> = DataPoints.getAssetAllocationTotalsByCategory(itemDAO.item, ASSET_ALLOCATION_GROUPS.EQUITY);
      this.addDisclaimerKeys(dpResponseAA.disclaimerKeys, itemDAO.item);

      const getLabelOverride = (key: string) =>
        (this.labelOverride && this.labelOverride[key] && this.labelOverride[key][this.locale]) || formatMessageWithLocale(key, this.locale);

      this.addItemField(itemDAO, getLabelOverride('Comparison.Table.EquityMetrics.EquityAllocation'), dpResponseAA.response, PercentageCell);

      const dpResponse: DataPointResponse<Dictionary<number | undefined>> = DataPoints.getEquityMetrics(itemDAO.item);
      this.addDisclaimerKeys(dpResponse.disclaimerKeys, itemDAO.item);

      this.addItemField(
        itemDAO,
        getLabelOverride('Comparison.Table.EquityMetrics.PBRatio'),
        dpResponse.response.pbRatio !== undefined ? dpResponse.response.pbRatio : 0,
        dpResponse.response.pbRatio !== undefined ? TextCell : NotApplicableCell,
      );

      this.addItemField(
        itemDAO,
        getLabelOverride('Comparison.Table.EquityMetrics.PERatio'),
        dpResponse.response.peRatio !== undefined ? dpResponse.response.peRatio : 0,
        dpResponse.response.peRatio !== undefined ? TextCell : NotApplicableCell,
      );

      this.addItemField(
        itemDAO,
        getLabelOverride('Comparison.Table.EquityMetrics.DistributionYield'),
        dpResponse.response.distributionYield !== undefined ? dpResponse.response.distributionYield : 0,
        dpResponse.response.distributionYield !== undefined ? PercentageCell : NotApplicableCell,
      );

      this.addItemField(
        itemDAO,
        getLabelOverride('Comparison.Table.EquityMetrics.ReturnOnEquity'),
        dpResponse.response.returnOnEquity !== undefined ? dpResponse.response.returnOnEquity : 0,
        dpResponse.response.returnOnEquity !== undefined ? PercentageCell : NotApplicableCell,
      );
    }
  }

  // private methods

  private addItemField(itemDAO: ComparisonItemDAO, name: string, value: number, cellType: any): void {
    const field: ProposalFieldDAO = new ProposalFieldDAO(name, value != null ? value.toFixed(2) : undefined, ProposalStyleClassNames.ProposalTableContent);
    field.cellType = cellType;
    this.addFieldDAO(itemDAO, field);
    this.addFieldName(name, ProposalStyleClassNames.ProposalTableContent);
  }
}
